import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Edit2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchJSON } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function AddPurchaseStock() {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();

    // URL Params
    const paramTankId = searchParams.get("tankId") || "";
    const paramDate = searchParams.get("date") || new Date().toISOString().split("T")[0];

    const [date, setDate] = useState<string>(paramDate);
    const [tankId, setTankId] = useState<string>(paramTankId);
    const [productName, setProductName] = useState<string>("");

    // Form Fields
    const [quantity, setQuantity] = useState<string>(""); // Receipt

    const [beforeDip, setBeforeDip] = useState<string>("");
    const [beforeStock, setBeforeStock] = useState<string>("");
    const [afterDip, setAfterDip] = useState<string>("");
    const [afterStock, setAfterStock] = useState<string>("");

    const [invoiceNo, setInvoiceNo] = useState<string>("");
    const [meterSale, setMeterSale] = useState<string>("");

    const [stockDifference, setStockDifference] = useState<string>("");
    const [stockDumped, setStockDumped] = useState<string>("");

    const [temp, setTemp] = useState<string>("");
    const [hydro, setHydro] = useState<string>("");
    const [density, setDensity] = useState<string>("");

    const [loading, setLoading] = useState(false);

    // Fetch Tanks
    const { data: tanks = [] } = useQuery({
        queryKey: ["/api/tanks-list"],
        queryFn: async () => {
            const response = await fetchJSON('/api/tanks-list');
            const result = await response.json();
            if (!result.ok) throw new Error(result.error || 'Failed to fetch tanks');
            return result.rows || [];
        },
        staleTime: 0,
        refetchOnMount: "always",
    });

    // Fetch existing data for this tank/date
    const { data: existingData } = useQuery({
        queryKey: ["/api/reports/opening-stock", date],
        queryFn: async () => {
            if (!date) return [];
            const res = await fetchJSON(`/api/reports/opening-stock?from=${date}&to=${date}`);
            const json = await res.json();
            if (!json.ok) return [];
            return json.rows || [];
        },
        enabled: !!date,
        staleTime: 0,
        refetchOnMount: "always",
    });

    // Fetch all stock dumps for the historical table
    const { data: allDumps = [] } = useQuery({
        queryKey: ["/api/reports/opening-stock", "all"],
        queryFn: async () => {
            const res = await fetchJSON(`/api/reports/opening-stock`);
            const json = await res.json();
            if (!json.ok) return [];
            return json.rows || [];
        },
        staleTime: 0,
        refetchOnMount: "always",
    });

    const tankDumps = allDumps.filter((d: any) => String(d.tank_id) === String(tankId));

    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(10);
    const [tableFilterText, setTableFilterText] = useState("");

    const filteredTableDumps = tankDumps.filter((row: any) => {
        if (!tableFilterText) return true;
        const q = tableFilterText.toLowerCase();
        return (
            String(row.stock_date || "").toLowerCase().includes(q) ||
            String(row.invoice_no || "").toLowerCase().includes(q) ||
            String(row.stock_received || "").toLowerCase().includes(q) ||
            String(row.density || "").toLowerCase().includes(q)
        );
    });

    const totalTablePages = Math.ceil(filteredTableDumps.length / tablePageSize) || 1;
    const startIndex = (tablePage - 1) * tablePageSize;
    const displayedTableDumps = filteredTableDumps.slice(startIndex, startIndex + tablePageSize);

    const handleEditRow = (row: any) => {
        setDate(row.stock_date || row.date || new Date().toISOString().split("T")[0]);
        setQuantity(row.stock_received?.toString() || "");
        setInvoiceNo(row.invoice_no || "");
        setBeforeDip(row.before_dip?.toString() || "");
        setBeforeStock(row.before_stock?.toString() || "");
        setAfterDip(row.after_dip?.toString() || "");
        setAfterStock(row.after_stock?.toString() || "");
        setStockDifference(row.stock_difference?.toString() || "");
        setStockDumped(row.stock_dumped?.toString() || "");
        setTemp(row.temp?.toString() || "");
        setHydro(row.hydrometer?.toString() || "");
        setDensity(row.density?.toString() || "");
        setMeterSale(row.meter_sale?.toString() || "");
        
        toast({
            title: "Row Loaded",
            description: `Loaded data for ${row.stock_date} to edit.`
        });
    };

    useEffect(() => {
        if (tankId && tanks.length > 0) {
            const tank = tanks.find((t: any) => t.id === tankId);
            if (tank) {
                setProductName(tank.product_name || tank.fuel_products?.product_name || "");
            }
        }
    }, [tankId, tanks]);

    // Pre-fill form if data exists
    useEffect(() => {
        if (existingData && existingData.length > 0 && tankId) {
            const row = existingData.find((r: any) => r.tank_id === tankId);
            if (row) {
                setQuantity(row.stock_received?.toString() || "");
                setInvoiceNo(row.invoice_no || "");
                setBeforeDip(row.before_dip?.toString() || "");
                setBeforeStock(row.before_stock?.toString() || "");
                setAfterDip(row.after_dip?.toString() || "");
                setAfterStock(row.after_stock?.toString() || "");
                setStockDumped(row.stock_dumped?.toString() || "");
                setStockDifference(row.stock_difference?.toString() || "");
                setTemp(row.temp?.toString() || "");
                setHydro(row.hydrometer?.toString() || "");
                setDensity(row.density?.toString() || "");
                setMeterSale(row.meter_sale?.toString() || "");
            }
        }
    }, [existingData, tankId]);

    // Auto-calculate Stock Difference when inputs change
    useEffect(() => {
        const bs = parseFloat(beforeStock) || 0;
        const qty = parseFloat(quantity) || 0;
        const ms = parseFloat(meterSale) || 0;
        const as = parseFloat(afterStock) || 0;
        
        if (beforeStock || quantity || meterSale || afterStock) {
            const bookStock = bs + qty - ms;
            const diff = as - bookStock;
            setStockDifference(diff.toFixed(2));
        }
    }, [beforeStock, quantity, meterSale, afterStock]);

    // Auto-fill Stock Dumped with Quantity by default
    useEffect(() => {
        if (quantity) {
            setStockDumped(quantity);
        }
    }, [quantity]);

    const handleSave = async () => {
        if (!tankId || !date) {
            toast({ variant: "destructive", title: "Missing Fields", description: "Date and Tank are required." });
            return;
        }

        try {
            setLoading(true);
            const payload = {
                date,
                tank_id: tankId,
                product_name: productName,
                quantity: quantity ? parseFloat(quantity) : 0,
                before_dip: beforeDip ? parseFloat(beforeDip) : null,
                before_stock: beforeStock ? parseFloat(beforeStock) : null,
                after_dip: afterDip ? parseFloat(afterDip) : null,
                after_stock: afterStock ? parseFloat(afterStock) : null,
                invoice_no: invoiceNo,
                meter_sale: meterSale ? parseFloat(meterSale) : null,
                stock_difference: stockDifference ? parseFloat(stockDifference) : null,
                stock_dumped: stockDumped ? parseFloat(stockDumped) : null,
                temp: temp ? parseFloat(temp) : null,
                hydro: hydro ? parseFloat(hydro) : null,
                density: density ? parseFloat(density) : null,
            };

            const res = await fetchJSON('/api/stock-dump', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            const result = await res.json();
            if (!result.ok) throw new Error(result.error);

            // Invalidate queries so that the main list and current page get refreshed data
            await queryClient.invalidateQueries({ queryKey: ["/api/reports/opening-stock"] });
            await queryClient.invalidateQueries({ queryKey: ["/api/tanks-list"] });

            toast({ title: "Success", description: "Stock Dump Saved Successfully" });
            navigate('/opening-stock'); // Go back to report
        } catch (e: any) {
            toast({ variant: "destructive", title: "Error", description: e.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => navigate('/opening-stock')}>
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">Add Purchase Stock</h1>
                </div>
                {tankId && (
                    <div className="flex items-center gap-3">
                        <span className="bg-emerald-500 text-white font-bold text-xs px-3 py-1.5 rounded uppercase tracking-wider">
                            Tank: {tanks.find((t: any) => t.id === tankId)?.tank_number || "Selected Tank"}
                        </span>
                        {productName && (
                            <span className="bg-blue-500 text-white font-bold text-xs px-3 py-1.5 rounded uppercase tracking-wider">
                                Product: {productName}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <Card className="bg-blue-600 text-white">
                <CardContent className="pt-6 space-y-4">
                    {/* Header Row */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3 space-y-1">
                            <Label className="text-white">Date</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white text-black" />
                        </div>
                        <div className="col-span-3 space-y-1">
                            <Label className="text-white">Tank</Label>
                            <Select value={tankId} onValueChange={setTankId}>
                                <SelectTrigger className="bg-white text-black"><SelectValue placeholder="Select Tank" /></SelectTrigger>
                                <SelectContent>
                                    {tanks.map((t: any) => (
                                        <SelectItem key={t.id} value={t.id}>{t.tank_number} - {t.product_name || "Unknown"}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-3 space-y-1">
                            <Label className="text-white">Product</Label>
                            <Input value={productName} readOnly className="bg-white text-black bg-opacity-90" />
                        </div>
                        <div className="col-span-3 space-y-1">
                            <Label className="text-white">Quantity (Lt)</Label>
                            <Input value={quantity} onChange={(e) => setQuantity(e.target.value)} className="bg-white text-black" placeholder="Receipt Qty" />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Before DIP</Label>
                            <Input value={beforeDip} onChange={(e) => setBeforeDip(e.target.value)} className="bg-white text-black" placeholder="Before DIP" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Before Stock (Lt)</Label>
                            <Input value={beforeStock} onChange={(e) => setBeforeStock(e.target.value)} className="bg-white text-black" placeholder="Before Stock" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">After DIP</Label>
                            <Input value={afterDip} onChange={(e) => setAfterDip(e.target.value)} className="bg-white text-black" placeholder="After DIP" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">After Stock (Lt)</Label>
                            <Input value={afterStock} onChange={(e) => setAfterStock(e.target.value)} className="bg-white text-black" placeholder="After Stock" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Invoice</Label>
                            <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} className="bg-white text-black" placeholder="Invoice No" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Meter Sale (Lt)</Label>
                            <Input value={meterSale} onChange={(e) => setMeterSale(e.target.value)} className="bg-white text-black" placeholder="0" />
                        </div>
                    </div>

                    {/* Row 3 */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3 space-y-1">
                            <Label className="text-white">Stock Difference</Label>
                            <Input value={stockDifference} onChange={(e) => setStockDifference(e.target.value)} className="bg-white text-black" placeholder="Difference" />
                        </div>
                        <div className="col-span-3 space-y-1">
                            <Label className="text-white">Stock Dumped</Label>
                            <Input value={stockDumped} onChange={(e) => setStockDumped(e.target.value)} className="bg-white text-black" placeholder="Dumped Qty" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Temp</Label>
                            <Input value={temp} onChange={(e) => setTemp(e.target.value)} className="bg-white text-black" placeholder="Temp" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Hydrometer.Read</Label>
                            <Input value={hydro} onChange={(e) => setHydro(e.target.value)} className="bg-white text-black" placeholder="Hydro" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white">Density</Label>
                            <Input value={density} onChange={(e) => setDensity(e.target.value)} className="bg-white text-black" placeholder="Density" />
                        </div>
                    </div>

                    <div className="flex justify-center pt-4">
                        <Button onClick={handleSave} disabled={loading} className="bg-green-500 hover:bg-green-600 text-white min-w-[200px] gap-2">
                            <Save className="w-4 h-4" /> {loading ? "SAVING..." : "SAVE"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Table Section */}
            <Card className="bg-white text-black shadow-md border border-gray-200 rounded-lg overflow-hidden">
                <CardContent className="pt-6 space-y-4">
                    {/* Table Header controls */}
                    <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <Select value={tablePageSize.toString()} onValueChange={(val) => { setTablePageSize(Number(val)); setTablePage(1); }}>
                                <SelectTrigger className="w-20 bg-white border-gray-300"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="10">10</SelectItem>
                                    <SelectItem value="25">25</SelectItem>
                                    <SelectItem value="50">50</SelectItem>
                                </SelectContent>
                            </Select>
                            <span className="text-sm text-gray-500">records per page</span>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <Button variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50" onClick={() => {
                                const headers = ["S.No", "Date", "Quantity", "Invoice", "DIP (B/A)", "Stock (B/A)", "Meter Sale", "Density", "Stock Variation"];
                                const csvContent = "data:text/csv;charset=utf-8,"
                                    + headers.join(",") + "\n"
                                    + filteredTableDumps.map((row, index) => [
                                        index + 1,
                                        row.stock_date || row.date || '',
                                        row.stock_received || 0,
                                        `"${row.invoice_no || ''}"`,
                                        `"${row.before_dip || 0} / ${row.after_dip || 0}"`,
                                        `"${row.before_stock || 0} / ${row.after_stock || 0}"`,
                                        row.meter_sale || 0,
                                        row.density || 0,
                                        row.stock_difference || 0
                                    ].join(",")).join("\n");
                                const encodedUri = encodeURI(csvContent);
                                const link = document.createElement("a");
                                link.setAttribute("href", encodedUri);
                                link.setAttribute("download", `stock_dumps_tank_${tankId}.csv`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}>CSV</Button>
                            <Button variant="outline" size="sm" className="border-red-500 text-red-600 hover:bg-red-50">PDF</Button>
                            
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Filter:</span>
                                <Input 
                                    placeholder="Type to filter..." 
                                    className="w-56 bg-white border-gray-300" 
                                    value={tableFilterText} 
                                    onChange={(e) => { setTableFilterText(e.target.value); setTablePage(1); }} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border border-gray-100 rounded-md">
                        <Table>
                            <TableHeader className="bg-gray-50 border-b border-gray-200">
                                <TableRow>
                                    <TableHead className="font-bold text-gray-700">S.No</TableHead>
                                    <TableHead className="font-bold text-gray-700">Date</TableHead>
                                    <TableHead className="font-bold text-gray-700">Quantity</TableHead>
                                    <TableHead className="font-bold text-gray-700">Invoice</TableHead>
                                    <TableHead className="font-bold text-gray-700">DIP</TableHead>
                                    <TableHead className="font-bold text-gray-700">Stock</TableHead>
                                    <TableHead className="font-bold text-gray-700">Meter Sale</TableHead>
                                    <TableHead className="font-bold text-gray-700">Density</TableHead>
                                    <TableHead className="font-bold text-gray-700">Stock Variation</TableHead>
                                    <TableHead className="font-bold text-gray-700 text-center">Edit</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {displayedTableDumps.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} className="h-24 text-center text-gray-400">
                                            No data available in table
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    displayedTableDumps.map((row: any, index: number) => (
                                        <TableRow key={row.id || index} className="hover:bg-slate-50 transition-colors">
                                            <TableCell className="font-medium">{startIndex + index + 1}</TableCell>
                                            <TableCell>{row.stock_date || row.date}</TableCell>
                                            <TableCell>{Number(row.stock_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                            <TableCell>{row.invoice_no || "-"}</TableCell>
                                            <TableCell>
                                                <span className="text-xs text-gray-500 block">B: {row.before_dip ?? "-"}</span>
                                                <span className="text-xs text-gray-800 font-medium block">A: {row.after_dip ?? "-"}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-xs text-gray-500 block">B: {Number(row.before_stock || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                <span className="text-xs text-gray-800 font-medium block">A: {Number(row.after_stock || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </TableCell>
                                            <TableCell>{Number(row.meter_sale || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                            <TableCell>{row.density ?? "-"}</TableCell>
                                            <TableCell className={Number(row.stock_difference || 0) < 0 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                                                {Number(row.stock_difference || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Button variant="ghost" size="sm" className="hover:bg-blue-50 text-blue-600" onClick={() => handleEditRow(row)}>
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between pt-2">
                        <div className="text-sm text-gray-500">
                            Showing {filteredTableDumps.length > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + tablePageSize, filteredTableDumps.length)} of {filteredTableDumps.length} entries
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                disabled={tablePage === 1} 
                                onClick={() => setTablePage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </Button>
                            <span className="text-sm font-semibold px-3 py-1 bg-blue-50 text-blue-600 rounded-md">
                                {tablePage}
                            </span>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                disabled={tablePage >= totalTablePages} 
                                onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

